import React from 'react';
import { useCombobox } from 'downshift';
import styled, { useTheme, css } from 'styled-components/macro';
import { SPACINGS, useDebounce } from '../../utils';
import { IconLoader, IconChevron } from '../../icons';
import {
  weightMap,
  sizesMap,
  textLineHeight,
  textLetterSpacing,
} from '../../typography/utils';
import { Text } from '../../typography';
import { useTranslate } from '@federico.moretti/react-translate';
import { TYPOGRAPHY_WEIGHT } from '../../typography/types';

const SelectContainer = styled.div`
  width: ${(p) => p.theme.dimensions.inputWidth};
`;

type SelectStyledType = {
  isMenuOpen: boolean;
  disabled: boolean;
  isFocused: boolean;
  error: boolean;
};

const SelectWrapperStyled = styled.div`
  position: relative;

  &:focus {
    outline: none;
  }
`;

const MenuWrapper = styled.ul`
  margin: 0;
  padding: 0;
  border-top: 0;
  outline: none;
  position: absolute;
  right: 0;
  left: 0;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const MenuStyled = styled.div`
  outline: none;
  padding: 0;
  margin: 0;
  margin-top: 3px;
  width: 100%;
  max-height: 10em;
  overflow: auto;
  background-color: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.black};
  border-radius: 0 0
    ${(p) =>
      `${p.theme.dimensions.borderRadius} ${p.theme.dimensions.borderRadius}`};
  z-index: ${(p) => p.theme.zIndexes.overlay};
  box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primary};

  transition: border 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

type ItemStyledProps = {
  weight: TYPOGRAPHY_WEIGHT;
  highlighted?: boolean;
  colorType?: 'placeholder';
};
const ItemStyled = styled.li<ItemStyledProps>`
  display: flex;
  align-items: center;
  list-style: none;
  color: ${(p) =>
    p.colorType === 'placeholder'
      ? p.theme.colors.secondary80w
      : p.theme.colors.black};
  height: calc(${SPACINGS[10]} - 2px);
  padding: 0 ${SPACINGS[5]};
  background-color: ${(p) =>
    p.highlighted ? p.theme.colors.primary80w : p.theme.colors.white};

  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-weight: ${(p) => weightMap[p.weight]};
  font-size: ${(p) => sizesMap.base.size};
  line-height: ${textLineHeight};
  letter-spacing: ${textLetterSpacing};
`;

const InputWrapper = styled.div`
  position: relative;
  outline: none;
  width: 100%;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input<SelectStyledType>`
  position: relative;
  height: ${SPACINGS[10]};
  width: 100%;
  padding: 0 ${SPACINGS[5]};

  outline: none;
  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.secondary80w)};
  border-radius: ${(p) => (p.isMenuOpen ? '10px 10px 0 0' : '10px')};
  background-color: ${(p) =>
    p.disabled ? p.theme.colors.secondary90w : 'transparent'};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};

  ${(p) =>
    p.isMenuOpen &&
    css`
      box-shadow: 0 0 0 2px ${p.theme.colors.primary};
      border: 1px solid
        ${p.error ? p.theme.colors.danger : p.theme.colors.secondary80w};
    `}

  &:focus {
    box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primary};
    border: 1px solid
      ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.black)};
  }

  &:hover {
    border: 1px solid ${(p) => p.theme.colors.black};
  }

  &::placeholder {
    color: ${(p) => p.theme.colors.secondary80w};
  }

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-weight: ${(p) => weightMap.normal};
  font-size: ${(p) => sizesMap.base.size};
  line-height: ${textLineHeight};
  letter-spacing: ${textLetterSpacing};
`;

export const CloseButton = styled.button`
  position: absolute;
  right: 10px;
  background-color: transparent;
  border: none;
`;

export const Loader = styled.button`
  position: absolute;
  right: 5px;
  background-color: transparent;
  border: none;
`;

type AsyncSelectProps<Item> = {
  itemToString: (i: Item | null) => string;
  onItemChange: (i: Item | null | undefined) => void;
  api: ((v: string | undefined) => Promise<Item[]>) | undefined;
  onInputValueChange?: (v: string | undefined) => void;
  rowComponent?: (i: Item | null) => React.ReactNode;
  id?: string;
  name?: string;
  label?: string | ((p: any) => React.ReactNode);
  error?: string | boolean;
  clearItemAfterChange?: boolean;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  testid?: string;
  clearInputOnChange?: boolean;
  initialInputValue?: string;
};
function AsyncSelect<T>(props: AsyncSelectProps<T>) {
  const {
    name,
    api,
    itemToString,
    onItemChange,
    onInputValueChange = () => {},
    initialInputValue = '',
    placeholder = 'Select...',
    style,
    testid = 'async-select',
    id,
    clearInputOnChange,
    disabled,
    error,
    label,
  } = props;

  const theme = useTheme();
  const { t } = useTranslate();

  const [data, setData] = React.useState<T[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const newId = name ?? id;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    setInputValue,
    selectedItem,
    getComboboxProps,
    getLabelProps,
  } = useCombobox({
    id: newId,
    stateReducer: (state, actionAndChanges) => {
      switch (actionAndChanges.type) {
        case useCombobox.stateChangeTypes.InputChange:
          return {
            ...actionAndChanges.changes,
            selectedItem:
              actionAndChanges.changes.inputValue === ''
                ? null
                : state.selectedItem,
          };
        default:
          return actionAndChanges.changes;
      }
    },
    onSelectedItemChange: (changes) => {
      setTimeout(() => onItemChange(changes.selectedItem));
      if (clearInputOnChange) {
        setInputValue('');
      }
    },
    onInputValueChange: (changes) => {
      if (changes.inputValue) {
        onInputValueChange(changes.inputValue);
      }
    },
    initialInputValue,
    items: data,
    itemToString,
  });

  const debouncedInputValue = useDebounce(inputValue, 500);

  React.useEffect(() => {
    let ignore = false;

    async function fetchApi(value: string) {
      if (api) {
        const res = await api(value);
        if (!ignore) {
          setData(res);
          setIsLoading(false);
        }
      }
    }

    if (api && debouncedInputValue !== '') {
      setIsLoading(true);
      fetchApi(debouncedInputValue);
    }

    return () => {
      ignore = true;
    };
  }, [api, debouncedInputValue]);

  return (
    <SelectContainer style={style}>
      {label && (
        <>
          {typeof label === 'string' ? (
            <Text type="label" {...getLabelProps()}>
              {label}
            </Text>
          ) : (
            label(getLabelProps())
          )}
        </>
      )}
      <SelectWrapperStyled>
        <InputWrapper {...getComboboxProps()}>
          <SearchInput
            id={newId}
            data-testid={`${testid}-input`}
            placeholder={placeholder}
            error={error}
            isMenuOpen={isOpen}
            {...getInputProps({ disabled })}
          />
          {isLoading ? (
            <Loader>
              <IconLoader stroke={theme.colors.primary} />
            </Loader>
          ) : (
            <CloseButton
              type="button"
              {...getToggleButtonProps({ disabled })}
              data-testid={`${testid}-menu-button`}
            >
              <IconChevron
                stroke={theme.colors.primary20b}
                direction={isOpen ? 'top' : 'bottom'}
              />
            </CloseButton>
          )}
        </InputWrapper>
        <MenuWrapper {...getMenuProps()} data-testid={`${testid}-menu`}>
          {isOpen ? (
            <MenuStyled>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <ItemStyled
                    data-testid={`${testid}-item-${index}`}
                    highlighted={highlightedIndex === index}
                    weight={
                      itemToString(item) === itemToString(selectedItem)
                        ? 'bold'
                        : 'normal'
                    }
                    key={index}
                    {...getItemProps({
                      item,
                      index,
                    })}
                  >
                    {itemToString(item)}
                  </ItemStyled>
                ))
              ) : (
                <ItemStyled weight="light" colorType="placeholder">
                  {t('noOptions')}
                </ItemStyled>
              )}
            </MenuStyled>
          ) : null}
        </MenuWrapper>
      </SelectWrapperStyled>
      {typeof error === 'string' && (
        <Text
          id={`${newId}-error`}
          style={{ color: theme.colors.danger, marginTop: SPACINGS[2] }}
          margin="none"
        >
          {error}
        </Text>
      )}
    </SelectContainer>
  );
}

export default AsyncSelect;
